// Filtros globales para Vue 3 (reemplazo de filtros de Vue 2)

/**
 * Obtiene la moneda oficial desde sysconf
 * @returns {string} - Símbolo de moneda oficial o ₡ por defecto
 */
function getOfficialCurrency() {
  try {
    // Intentar obtener desde sessionStorage primero
    const sysconf = JSON.parse(sessionStorage.getItem('sysconf') || '{}')
    if (sysconf && sysconf.official_currency) {
      return sysconf.official_currency
    }
    
    // Fallback: intentar desde localStorage
    const sysconfLocal = JSON.parse(localStorage.getItem('sysconf') || '{}')
    if (sysconfLocal && sysconfLocal.official_currency) {
      return sysconfLocal.official_currency
    }
    
    // Fallback final
    return '₡'
  } catch (error) {
    console.warn('Error al obtener official_currency de sysconf:', error)
    return '₡'
  }
}

/**
 * Formatea un número como moneda usando la moneda oficial del sistema
 * @param {number|string} value - El valor a formatear
 * @param {string} currency - El símbolo de moneda (opcional, usa official_currency por defecto)
 * @param {number} decimals - Número de decimales (por defecto 2)
 * @returns {string} - Valor formateado como moneda
 */
export function formatMoney(value, currency = null, decimals = 2) {
  // Si no se especifica moneda, usar la oficial del sistema
  const finalCurrency = currency || getOfficialCurrency()
  
  if (value === null || value === undefined || value === '') {
    return `${finalCurrency}0.00`
  }

  const numValue = parseFloat(value)
  
  if (isNaN(numValue)) {
    return `${finalCurrency}0.00`
  }

  return `${finalCurrency}${numValue.toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
}

/**
 * Formatea un número como moneda usando específicamente la moneda oficial del sistema
 * @param {number|string} value - El valor a formatear
 * @param {number} decimals - Número de decimales (por defecto 2)
 * @returns {string} - Valor formateado con moneda oficial
 */
export function formatOfficialMoney(value, decimals = 2) {
  return formatMoney(value, getOfficialCurrency(), decimals)
}

/**
 * Formatea un número como porcentaje
 * @param {number|string} value - El valor a formatear
 * @param {number} decimals - Número de decimales (por defecto 2)
 * @returns {string} - Valor formateado como porcentaje
 */
export function formatPercent(value, decimals = 2) {
  if (value === null || value === undefined || value === '') {
    return '0.00%'
  }

  const numValue = parseFloat(value)
  
  if (isNaN(numValue)) {
    return '0.00%'
  }

  return `${numValue.toFixed(decimals)}%`
}

/**
 * Formatea un número con separadores de miles
 * @param {number|string} value - El valor a formatear
 * @param {number} decimals - Número de decimales (por defecto 0)
 * @returns {string} - Valor formateado con separadores
 */
export function formatNumber(value, decimals = 0) {
  if (value === null || value === undefined || value === '') {
    return '0'
  }

  const numValue = parseFloat(value)
  
  if (isNaN(numValue)) {
    return '0'
  }

  return numValue.toFixed(decimals).replace(/\d(?=(\d{3})+\.)/g, '$&,')
}

/**
 * Formatea fecha en formato legible
 * @param {string|Date} value - La fecha a formatear
 * @param {string} format - Formato deseado (por defecto 'DD/MM/YYYY')
 * @returns {string} - Fecha formateada
 */
export function formatDate(value, format = 'DD/MM/YYYY') {
  if (!value) return ''

  const date = new Date(value)
  
  if (isNaN(date.getTime())) {
    return ''
  }

  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`
    case 'DD/MM/YYYY HH:mm':
      return `${day}/${month}/${year} ${hours}:${minutes}`
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`
    default:
      return `${day}/${month}/${year}`
  }
}

// Exportación por defecto con todos los filtros
export default {
  formatMoney,
  formatOfficialMoney,
  formatPercent,
  formatNumber,
  formatDate
}